import { useState, useCallback } from 'react';
import { useSnackbar } from "notistack";
import { IApiResponse } from 'api/models/IApiResponse';
import { useTranslation } from 'react-i18next';
import { RoutePaths } from 'constants/RoutePaths';
import { ApiCallFunction } from 'api/models/ApiCallFunction';
import useRefreshToken from 'hooks/useRefreshToken';

const useApiCall = <RequestType, ResponseType>(
  apiCallFunction: ApiCallFunction<RequestType, IApiResponse<ResponseType>>,
  successMessageKey?: string,
  errorMessageKey?: string,
  useRefreshResponse?: boolean
) => {
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { refreshToken, loading: refreshingToken } = useRefreshToken();

  const notifyApiError = useCallback(
    (errorCode: string, errorData?: string) => {
      enqueueSnackbar(t(`api.${errorCode}`, { data: errorData }), { variant: 'error' });
    },
    [enqueueSnackbar, t]
  );

  const handleSuccessMessage = useCallback(() => {
    if (successMessageKey) {
      enqueueSnackbar(t(successMessageKey), { variant: 'success' });
    }
  }, [enqueueSnackbar, successMessageKey, t]);

  const handle401Error = useCallback(
    async (params: RequestType): Promise<IApiResponse<ResponseType>> => {
      const refreshResponse = await refreshToken();

      if (refreshResponse.success) {
        if (useRefreshResponse) {
          return {
            success: true,
            data: refreshResponse.profile as ResponseType,
            status: 200,
            message: 'Profile fetched from refresh token response',
            errors: undefined,
          };
        }
        return await apiCallFunction(params);
      } else {
        window.location.href = RoutePaths.LOGIN;
        return {
          success: false,
          data: undefined,
          status: 401,
          message: 'Unauthorized',
          errors: undefined,
        };
      }
    },
    [refreshToken, apiCallFunction, useRefreshResponse]
  );

  const handleApiError = useCallback(
    (e: any, notifyApiError: (errorCode: string, errorData?: string) => void) => {
      const errorCode = e?.response?.status || e?.status || 500;

      if (e?.response?.data?.errors) {
        e.response.data.errors.forEach((error: any) => {
          if (error.code === "IdentityError") {
            notifyApiError(e.response?.data.data.identityErrorCode);
          } else if (error.code === "LoginRateLimitExceeded") {
            const secondsToWait = parseInt(error.description);
            notifyApiError(error.code, (secondsToWait / 60).toString());
          }
           else {
            notifyApiError(error.code);
          }
          
        });
      } else {
        notifyApiError(errorCode, e.response?.data?.data);
      }

      return {
        success: false,
        data: e?.response?.data?.data,
        status: errorCode,
        message: e?.response?.message,
        errors: e?.response?.data?.errors,
      };
    },
    []
  );

  const callAPI = useCallback(
    async (params: RequestType): Promise<IApiResponse<ResponseType>> => {
      try {
        setLoading(true);
        const response = await apiCallFunction(params);
        handleSuccessMessage();
        return response;
      } catch (e: any) {
        if (!refreshingToken && e?.response?.status === 401) {
          return await handle401Error(params);
        }

        if (errorMessageKey) {
          enqueueSnackbar(t(errorMessageKey), { variant: 'error' });
        }

        return handleApiError(e, notifyApiError);
      } finally {
        setLoading(false);
      }
    },
    [
      apiCallFunction,
      handleSuccessMessage,
      handle401Error,
      handleApiError,
      enqueueSnackbar,
      t,
      refreshingToken,
      notifyApiError,
      errorMessageKey,
    ]
  );

  return { callAPI, loading };
};

export default useApiCall;